// ── Device Detection ──
// Detects device type, input mode, and gyroscope availability.

export type DeviceType = 'desktop' | 'mobile' | 'tablet';
export type InputMode = 'keyboard-mouse' | 'touch' | 'touch-gyro';

/** Detect device type using touch capability, screen size, and user agent. */
export function detectDevice(): DeviceType {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'desktop';
  }
  const ua = navigator.userAgent.toLowerCase();
  const rawUA = navigator.userAgent;
  const hasTouchScreen =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0;

  // MOB-L1 / F-CM-013: iPad detection. Modern iPads (iPadOS 13+) report a
  // desktop Mac UA by default. Disambiguate by combining `maxTouchPoints > 1`
  // with whichever platform signal is available. Prefer the modern
  // `navigator.userAgentData.platform` over the deprecated/spoofable
  // `navigator.platform`, then fall back to a UA-based `Macintosh` check so
  // the iPad isn't reclassified as desktop on Brave / privacy-focused
  // builds that return an empty `platform` string. We must NOT match the
  // generic "Mac OS X" substring used in iPhone/iPad UAs ("like Mac OS X")
  // — only accept platform hints that look like a desktop Mac
  // (`MacIntel`, `macOS`) or the literal `Macintosh` UA token.
  const uaData = (navigator as unknown as {
    userAgentData?: { platform?: string };
  }).userAgentData;
  const platformHint =
    (typeof uaData?.platform === 'string' ? uaData.platform : '') ||
    navigator.platform ||
    '';
  const looksLikeMac =
    /^(macOS|MacIntel|Mac)$/i.test(platformHint) ||
    /Macintosh/.test(rawUA);
  const isIPad =
    /iPad/.test(rawUA) ||
    (navigator.maxTouchPoints > 1 && looksLikeMac);

  // Check UA for common mobile/tablet identifiers
  const mobileUA = /android|iphone|ipod|windows phone|blackberry|opera mini|iemobile/.test(ua);
  const tabletUA = /ipad|android(?!.*mobile)|tablet|kindle|silk/.test(ua);

  if (isIPad || tabletUA) return 'tablet';
  if (mobileUA) return 'mobile';

  // Fallback: touch-capable device with small screen is likely mobile/tablet
  // But exclude devices with fine pointer / hover (touch-enabled laptops)
  if (hasTouchScreen) {
    const hasHover = window.matchMedia('(hover: hover)').matches;
    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
    if (hasHover || hasFinePointer) {
      // Touch-enabled laptop/desktop — treat as desktop
      return 'desktop';
    }
    const minDim = Math.min(window.innerWidth, window.innerHeight);
    if (minDim <= 480) return 'mobile';
    if (minDim <= 1024) return 'tablet';
  }

  return 'desktop';
}

/** Probe whether the device has a gyroscope via DeviceMotionEvent. */
export async function hasGyroscope(): Promise<boolean> {
  // iOS 13+ requires permission
  const dme = DeviceMotionEvent as unknown as {
    requestPermission?: () => Promise<string>;
  };
  if (typeof dme.requestPermission === 'function') {
    // Don't request here — just check if the API exists.
    // Actual permission must be requested from a user gesture.
    return true;
  }

  // On other platforms, probe with a short listener
  return new Promise<boolean>((resolve) => {
    let resolved = false;
    const handler = (e: DeviceMotionEvent) => {
      if (resolved) return;
      resolved = true;
      window.removeEventListener('devicemotion', handler);
      resolve(e.rotationRate !== null && e.rotationRate !== undefined);
    };
    window.addEventListener('devicemotion', handler, { once: true });

    // If no event fires within 500ms, assume no gyroscope
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        window.removeEventListener('devicemotion', handler);
        resolve(false);
      }
    }, 500);
  });
}

/** Get the recommended input mode for the current device. */
export function getInputMode(): InputMode {
  const device = detectDevice();
  if (device === 'desktop') return 'keyboard-mouse';
  // Default to touch; gyro can be enabled later via settings
  return 'touch';
}

/** Convenience helper: true if device is mobile or tablet. */
export function isMobile(): boolean {
  const device = detectDevice();
  return device === 'mobile' || device === 'tablet';
}
